import { useCallback } from 'react'
import { Document } from '../../end-of-year-review/types'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { IsMarkedNotRelevantFunction } from '../../end-of-year-review/not-relevant-documents'

/**
 * Check if a document's provider matches the requested provider.
 * Treats both undefined and '' as "no provider" for backward compatibility.
 */
function isProviderMatch(
  docProvider: string | undefined,
  provider: string | undefined
): boolean {
  const isProviderless = provider === undefined || provider === ''
  return isProviderless
    ? !docProvider || docProvider === ''
    : docProvider === provider
}

/**
 * Pure function: Filter documents by category, provider, and name.
 *
 * @param params - Filter parameters
 * @param params.stepContextDocuments - Documents from step context
 * @param params.userDocuments - User's uploaded documents
 * @param params.categoryInternalName - Document category code
 * @param params.provider - Optional provider name. For provider-less documents, omit or pass undefined
 * @param params.documentName - Optional specific document name
 *
 * @returns Array of user documents matching the criteria
 */
export function filterDocumentsImpl({
  stepContextDocuments,
  userDocuments,
  categoryInternalName,
  provider,
  documentName,
}: {
  stepContextDocuments: Document[]
  userDocuments: UserDocument[]
  categoryInternalName: string
  provider?: string
  documentName?: string
}): UserDocument[] {
  const uploadedIds = stepContextDocuments
    .filter((doc) => {
      if (doc.categoryCode !== categoryInternalName) return false
      if (documentName !== undefined && doc.name !== documentName) return false
      if (doc.notRelevant) return false
      return isProviderMatch(doc.provider, provider)
    })
    .map((doc) => doc.documentId)
  return userDocuments.filter((doc) => uploadedIds.includes(doc.id))
}

/**
 * Pure function: Get a single document by category, provider, and name.
 *
 * @param params - Query parameters
 * @param params.stepContextDocuments - Documents from step context
 * @param params.categoryInternalName - Document category code
 * @param params.provider - Optional provider name. For provider-less documents, omit or pass undefined
 * @param params.documentName - Optional specific document name
 *
 * @returns Document from step context, or a placeholder if not found
 */
export function getDocumentImpl({
  stepContextDocuments,
  categoryInternalName,
  provider,
  documentName,
}: {
  stepContextDocuments: Document[]
  categoryInternalName: string
  provider?: string
  documentName?: string
}): Document {
  const uploadedDoc = stepContextDocuments.find((doc) => {
    if (doc.categoryCode !== categoryInternalName) return false
    if (documentName !== undefined && doc.name !== documentName) return false
    return isProviderMatch(doc.provider, provider)
  })

  return (
    uploadedDoc ?? {
      categoryCode: categoryInternalName,
      provider,
      name: documentName,
    }
  )
}

/**
 * Pure function: Get the status of a document category.
 *
 * @param params - Status query parameters
 * @param params.stepContextDocuments - Documents from step context
 * @param params.userDocuments - User's uploaded documents
 * @param params.categoryInternalName - Document category code
 * @param params.provider - Optional provider name. For provider-less documents, omit or pass undefined
 * @param params.isMarkedNotRelevant - Optional function to check if category is marked not relevant
 * @param params.documentName - Optional specific document name
 *
 * @returns 'success' if documents exist or marked not relevant, 'none' otherwise
 */
export function getStatusImpl({
  stepContextDocuments,
  userDocuments,
  categoryInternalName,
  provider,
  isMarkedNotRelevant,
  documentName,
}: {
  stepContextDocuments: Document[]
  userDocuments: UserDocument[]
  categoryInternalName: string
  provider?: string
  isMarkedNotRelevant?: IsMarkedNotRelevantFunction
  documentName?: string
}): 'success' | 'none' {
  const hasDocs =
    filterDocumentsImpl({
      stepContextDocuments,
      userDocuments,
      categoryInternalName,
      provider,
      documentName,
    }).length > 0
  const notRelevant = isMarkedNotRelevant?.(
    categoryInternalName,
    provider,
    documentName
  )
  return hasDocs || notRelevant ? 'success' : 'none'
}

/**
 * Hook for filtering and querying documents from step context.
 *
 * Provides utility functions for working with documents after they've been fetched.
 * Takes already-fetched data as input and returns helper functions for filtering,
 * status checking, and document lookup.
 *
 * @param stepContextDocuments - Documents from step context (already fetched)
 * @param userDocuments - User's uploaded documents (already fetched)
 *
 * @example
 * // Provider-less documents (Tax Return, Assets, Other)
 * const { filterDocuments, getStatus } = useDocuments({...})
 * const docs = filterDocuments(categoryCode)  // Omit provider parameter
 * const status = getStatus(categoryCode, undefined)
 *
 * @example
 * // Provider-based documents (Payroll, Contractor)
 * const docs = filterDocuments(categoryCode, 'Gusto')  // Explicit provider
 * const status = getStatus(categoryCode, 'Gusto')
 */
export const useDocuments = ({
  stepContextDocuments = [],
  userDocuments = [],
}: {
  stepContextDocuments?: Document[]
  userDocuments: UserDocument[]
}) => {
  /**
   * Filter documents by category, provider, and name.
   *
   * @param categoryInternalName - Document category code
   * @param provider - Optional provider name. For provider-less documents, omit this parameter.
   * @param documentName - Optional specific document name
   *
   * @returns Array of user documents matching the criteria
   */
  const filterDocuments = useCallback(
    (categoryInternalName: string, provider?: string, documentName?: string) =>
      filterDocumentsImpl({
        stepContextDocuments,
        userDocuments,
        categoryInternalName,
        provider,
        documentName,
      }),
    [stepContextDocuments, userDocuments]
  )

  /**
   * Get a single document by category, provider, and name.
   *
   * @param categoryInternalName - Document category code
   * @param provider - Optional provider name. For provider-less documents, omit this parameter.
   * @param documentName - Optional specific document name
   *
   * @returns Document from step context, or a placeholder if not found
   */
  const getDocument = useCallback(
    (categoryInternalName: string, provider?: string, documentName?: string) =>
      getDocumentImpl({
        stepContextDocuments,
        categoryInternalName,
        provider,
        documentName,
      }),
    [stepContextDocuments]
  )

  /**
   * Get the status of a document category ('success' if uploaded or marked not relevant, 'none' otherwise).
   *
   * @param categoryInternalName - Document category code
   * @param provider - Optional provider name. For provider-less documents, omit this parameter.
   * @param isMarkedNotRelevant - Optional function to check if category is marked not relevant
   * @param documentName - Optional specific document name
   *
   * @returns 'success' if documents exist or marked not relevant, 'none' otherwise
   */
  const getStatus = useCallback(
    (
      categoryInternalName: string,
      provider?: string,
      isMarkedNotRelevant?: IsMarkedNotRelevantFunction,
      documentName?: string
    ) =>
      getStatusImpl({
        stepContextDocuments,
        userDocuments,
        categoryInternalName,
        provider,
        isMarkedNotRelevant,
        documentName,
      }),
    [stepContextDocuments, userDocuments]
  )

  return {
    filterDocuments,
    getStatus,
    getDocument,
  }
}
